/**
 * Visible, crawlable explanation of a bundle as a first-class product entity:
 * what it is, which formulas it contains (as real links), why they are paired,
 * who it is for and how it differs from Ova Balance on its own.
 *
 * Copy comes from the canonical ecosystem config so the visible page and the
 * structured data can never disagree.
 */
import { Link } from "@tanstack/react-router";
import { CORE_PRODUCTS, type EcosystemBundle } from "@/lib/ovaEcosystem";

export function BundleEcosystemSection({ bundle }: { bundle: EcosystemBundle }) {
  return (
    <section
      className="mx-auto mt-14 max-w-5xl px-4 md:px-8"
      aria-labelledby="bundle-ecosystem-title"
    >
      <h2
        id="bundle-ecosystem-title"
        className="font-display text-2xl font-bold text-brand-deep md:text-3xl"
      >
        What is the {bundle.name}?
      </h2>
      <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground md:text-base">
        {bundle.summary}
      </p>

      <h3 className="mt-8 text-sm font-semibold uppercase tracking-[0.14em] text-brand-deep/70">
        Products included
      </h3>
      <ul className="mt-3 grid gap-3 sm:grid-cols-3">
        {bundle.components.map((handle) => (
          <li key={handle}>
            <Link
              to="/products/$handle"
              params={{ handle }}
              className="block rounded-2xl border border-border bg-card px-4 py-3 text-sm font-semibold text-brand-deep transition hover:border-brand/40"
            >
              {CORE_PRODUCTS[handle].name}
            </Link>
          </li>
        ))}
      </ul>

      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <Block title="Why these are paired" body={bundle.whyPaired} />
        <Block title="Who this is for" body={bundle.whoFor} />
        <Block title="How it differs from Ova Balance alone" body={bundle.versusCore} />
        <Block
          title="How it fits a daily routine"
          body="Follow each formula's own directions as printed on its pack and product page; the combination does not change how any single formula is taken."
        />
      </div>
    </section>
  );
}

function Block({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <h3 className="text-sm font-semibold text-brand-deep">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}
